import { Controller } from "@hotwired/stimulus"
import { scrollContainerFor } from "../utils/scroll_container"

export default class extends Controller {
  static targets = ["button"]
  // A reader is asked for in screenfuls ("once they are a screen deep"), which is a
  // different number on a phone than on a desktop; everywhere else names its own pixel depth.
  static values = { threshold: { type: Number, default: 400 }, viewports: Number, yieldTo: String }

  connect() {
    this.scrollHandler = this.throttle(() => {
      this.updateVisibility()
    }, 16)

    // Capture phase: in the admin area the page scroll belongs to an element, not the document.
    window.addEventListener('scroll', this.scrollHandler, true)

    // A page can open already scrolled — an in-page anchor, or a restored position.
    this.updateVisibility()
  }

  disconnect() {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler, true)
    }
  }

  scrollToTop() {
    scrollContainerFor(this.element).scrollTo({
      top: 0,
      behavior: this.prefersReducedMotion ? 'auto' : 'smooth'
    })
  }

  updateVisibility() {
    if (!this.hasButtonTarget) return

    const scroller = scrollContainerFor(this.element)

    if (scroller.scrollTop > this.showThresholdFor(scroller) && !this.yieldTargetOnScreen) {
      this.buttonTarget.classList.add('visible')
    } else {
      this.buttonTarget.classList.remove('visible')
    }
  }

  // The disc floats over the column it is centred on, so it steps aside while a control
  // that shares that strip (the pager under the step, TMS-1831) is on screen;
  // otherwise a tap meant for Next lands on Back to top.
  get yieldTargetOnScreen() {
    if (!this.yieldToValue) return false

    const target = document.querySelector(this.yieldToValue)
    if (!target || target.getClientRects().length === 0) return false

    const rect = target.getBoundingClientRect()
    return rect.top < window.innerHeight && rect.bottom > 0
  }

  showThresholdFor(scroller) {
    if (this.viewportsValue > 0) return this.viewportsValue * scroller.clientHeight

    return this.thresholdValue
  }

  get prefersReducedMotion() {
    return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  }

  // Throttled with a trailing call, not just a leading one: a reader who flicks the page
  // and stops mid-window would otherwise have their last scroll event dropped, leaving the
  // button describing a position they have already left.
  throttle(callback, limit) {
    let timer = null
    let missed = false

    const invoke = () => {
      callback()
      timer = setTimeout(() => {
        timer = null
        if (!missed) return

        missed = false
        invoke()
      }, limit)
    }

    return () => {
      if (timer) {
        missed = true
        return
      }

      invoke()
    }
  }
}
